/* eslint-disable array-element-newline */

import React from "react";
import { useQuery } from "react-query";
import useDebounce from "./useDebounce";

const getPagesCount = (totalCount, pageSize) =>
	Math.ceil(totalCount / pageSize);

export const usePagination = ({
	debounceDelay = 0,
	queryKey,
	dataGetter,
	pageSize = 20,
	initialPage = 0,
	extraDeps = [],
	keepPreviousData = true,
	...options
}) => {
	const [currentPageIndex, setCurrentPageIndex] = React.useState(initialPage);

	//eslint-disable-next-line
  const deps = React.useMemo(() => [queryKey, currentPageIndex, pageSize, dataGetter, ...extraDeps], [
			queryKey,
			currentPageIndex,
			pageSize,
			dataGetter,
			//eslint-disable-next-line
    ...extraDeps,
		]
	);

	const debouncedDeps = useDebounce(deps, debounceDelay);

	const {
		data: response,
		isLoading: loading,
		error,
		refetch,
		...rest
	} = useQuery(
		[...debouncedDeps],
		async () => {
			const limit = pageSize;
			const offset = limit * currentPageIndex;

			const res = await dataGetter(limit, offset);

			// check if real count of pages is less than current page
			const pagesCount = getPagesCount(res.totalCount, pageSize);
			if (pagesCount < currentPageIndex + 1) {
				loadPage(Math.max(pagesCount - 1, 0));
			}

			return res;
		},
		{ keepPreviousData, ...options }
	);

	const loadPage = React.useCallback(setCurrentPageIndex, [
		setCurrentPageIndex
	]);

	return {
		loading,
		error,
		data: response?.data,
		loadPage,
		totalCount: response?.totalCount || 0,
		currentPage: currentPageIndex,
		pageSize,
		refetch,
		debouncedDeps,
		...rest
	};
};
